import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import Breadcrumb, { BreadcrumbItemType, BreadcrumbSeparatorType } from 'antd/es/breadcrumb/Breadcrumb';

import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import LinkMore from '@app/components/UI/LinkMore/LinkMore';
import imagePlaceholder from '@app/images/image-placeholder.png';
import { CustomMenuItem } from '@app/objects/CustomMenuItem';
import '@app/scss/components/masthead.scss';

interface MastheadBaseProps {
	title: string | React.ReactElement;
	imageSrc: string;
	imageAlt?: string;
	text?: string;
	quoteAuthor?: string;
	buttonText?: string | React.ReactElement;
	breadcrumbs?: Array<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>> | undefined;
	backPath?: CustomMenuItem;
	btnPath?: string;
	onBtnClick?: any;
	to?: string;
	other?: React.ReactNode;
	lazy?: boolean;
}

type MastheadProps = MastheadBaseProps &
	({ text: string; quoteAuthor: string } | { text: string } | { imageSrc: string } | { breadcrumbs: Array<CustomMenuItem>; backPath: CustomMenuItem });

const Masthead: React.FC<MastheadProps> = (props) => {
	const [loaded, setLoaded] = React.useState(false);
	const mastheadRef = React.useRef<any>();
	const { lazy = false } = props;

	const itemRender = (currentRoute: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>): React.ReactNode => {
		return currentRoute.path ? <Link to={currentRoute.path}>{currentRoute.title}</Link> : currentRoute.title;
	};

	function onLoad() {
		setLoaded(true);
	}

	React.useEffect(() => {
		mastheadRef.current.scrollIntoView({ block: 'end', inline: 'nearest' });
	}, []);

	return (
		<>
			<Helmet>
				<meta property="og:image" content={`https://www.lapoflove.com/${props.imageSrc}`.replace('com//', 'com/')} />
				<meta name="twitter:image" content={`https://www.lapoflove.com/${props.imageSrc}`.replace('com//', 'com/')} />
			</Helmet>
			<div className="masthead">
				<div className="masthead-wrapper" ref={mastheadRef!}>
					<div className={!loaded ? 'masthead-body' : 'masthead-body masthead-body_loaded'}>
						<div className="masthead-body__left">
							{props.breadcrumbs && <Breadcrumb items={props.breadcrumbs} itemRender={itemRender} />}
							<h1 className="masthead-body__title">{props.title}</h1>
							{props.quoteAuthor && (
								<blockquote className="masthead-body-quote">
									<p className="masthead-body-quote__text">{`“${props.text}”`}</p>
									<footer className="masthead-body-quote__footer">{`~ ${props.quoteAuthor}`}</footer>
								</blockquote>
							)}

							<div className="masthead-body-block">
								{props.text && !props.quoteAuthor && <p className="masthead-body-quote__text-sm">{props.text}</p>}
								{props.other && props.other}
								{props.buttonText && (
									(typeof props.buttonText === 'string' ? (
										<Link onClick={() => props.onBtnClick(props.btnPath)} to={props.to || ''}>
											<button type="button" className="btn btn-primary">
												{props.buttonText}
											</button>
										</Link>
									) : (
										props.buttonText
									))
								)}
							</div>
							{props.backPath && (
								<LinkMore
									linkMoreHref={props.backPath.path}
									classNameAdd="masthead-link-back"
									linkBack
									linkMoreCaption={props.backPath.name}
								/>
							)}
						</div>
						<div className="masthead-body__image-wrapper">
							<CustomAvatar
								className="masthead-body__image"
								src={props.imageSrc}
								alt={props?.imageAlt || 'Avatar'}
								errorAvatar={imagePlaceholder}
								maxWidth={600}
								maxHeight={450}
								lazy={lazy}
								fetchPriority="high"
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Masthead;
